import { toActionStatusModel } from '../model/mappers/action-status.model';
import { toActionModel } from '../model/mappers/action.model';
import { toActionSearchRequest } from '../model/mappers/search-request.dto';

import type { Action } from '../model/types/action.dto';
import type { ActionModel } from '../model/types/action.model';
import type { ActionStatusDto } from '../model/types/actions-status.dto';
import type { ActionStatusModel } from '../model/types/actions-status.model';
import type { ActionSearchRequestDto } from '../model/types/search-request.dto';
import type { ActionSearchRequestModel } from '../model/types/search-request.model';
import type { ActionStatusSearchModel } from '../model/types/status-request.model';
import type { ActionStatusResponseModel } from '../model/types/status-response.model';
import type { RootState } from '@/app/store';
import type { MessageCreateModel } from '@/features/Shared';
import type { SearchResponseDto } from '@/features/Shared/model/types/search-response.dto';
import type { SearchResponseModel } from '@/features/Shared/model/types/search-response.model';
import type { FetchBaseQueryError, QueryReturnValue } from '@reduxjs/toolkit/query';

import { apiSlice } from '@/app/api/api';
import { toMessageCreateDto } from '@/features/Shared/model/mappers/message-create.dto';

export const actionsApiSlice = apiSlice.injectEndpoints({
	endpoints: (builder) => ({
		actions: builder.infiniteQuery<
			SearchResponseModel<ActionModel>,
			ActionSearchRequestModel,
			string | undefined
		>({
			infiniteQueryOptions: {
				initialPageParam: undefined,
				getNextPageParam: (lastPage: SearchResponseModel<ActionModel>) => {
					return lastPage.moreResults ? (lastPage.continuation ?? undefined) : undefined;
				},
			},

			query: ({ queryArg, pageParam }) => ({
				url: '/actions/search',
				method: 'POST',
				body: {
					...toActionSearchRequest(queryArg),
					continuation_token: pageParam ?? undefined,
				} satisfies ActionSearchRequestDto,
			}),
			transformResponse: (response: SearchResponseDto<Action>) => {
				return {
					items: (response.items ?? []).map(toActionModel),
					continuation: response.meta.continuation,
					moreResults: response.meta.more_results,
				};
			},
		}),
		actionStatuses: builder.query<ActionStatusResponseModel, ActionStatusSearchModel>({
			queryFn: async (arg, api, _extraOptions, baseQuery) => {
				try {
					const state = api.getState() as RootState;
					const existingIds =
						actionsApiSlice.endpoints.actionStatuses.select(arg)(state)?.data
							?.actionsStatusMap ?? {};

					const requests = arg.organizations.map((org) => {
						return {
							organizationId: org.id,
							ids: org.actionsIds.filter((a) => !existingIds[a]),
						};
					});

					const results = (await Promise.all(
						requests.map(async (req) => {
							const response = await baseQuery({
								url: `/organizations/${req.organizationId}/actions/status/search`,
								method: 'POST',
								body: {
									ids: req.ids,
								},
							});
							return response;
						})
					)) as QueryReturnValue<ActionStatusDto[]>[];

					const hasError = results.some((result) => 'error' in result);
					if (hasError) {
						const error = results.find((result) => 'error' in result)?.error as
							| FetchBaseQueryError
							| undefined;
						return {
							error: {
								status: 'FETCH_ERROR',
								error: error instanceof Error ? error.message : 'Unknown error',
							},
						};
					}

					const actionsStatusMap: Record<string, ActionStatusModel> = {};
					results.forEach((result) => {
						if ('data' in result && result.data && Array.isArray(result.data)) {
							result.data.forEach((data) => {
								actionsStatusMap[data.id] = toActionStatusModel(data);
							});
						}
					});

					return {
						data: { actionsStatusMap },
					};
				} catch (error) {
					return {
						error: {
							status: 'FETCH_ERROR',
							error: error instanceof Error ? error.message : 'Unknown error',
						},
					};
				}
			},
			serializeQueryArgs: () => {
				return 'actions-status';
			},
			merge(currentCache, newData) {
				Object.assign(currentCache.actionsStatusMap, newData.actionsStatusMap);
			},
			forceRefetch() {
				return false;
			},
			providesTags: ['ORGANIZATIONS'],
		}),
		createActions: builder.mutation<void, MessageCreateModel>({
			query: (item) => {
				return {
					url: `/actions`,
					method: 'POST',
					body: toMessageCreateDto(item),
				};
			},
			invalidatesTags: () => [{ type: 'ACTIONS', id: 'PARTIAL-LIST' }],
		}),
		deleteActions: builder.mutation<void, { id: string; organizationId: string }>({
			query: ({ id, organizationId }) => {
				return {
					url: `/organizations/${organizationId}/actions/${id}`,
					method: 'DELETE',
				};
			},
			invalidatesTags: () => [{ type: 'ACTIONS', id: 'PARTIAL-LIST' }],
		}),
	}),

	overrideExisting: false,
});

export const {
	useActionsInfiniteQuery,
	useActionStatusesQuery,
	useCreateActionsMutation,
	useDeleteActionsMutation,
} = actionsApiSlice;
